import { TextField } from '@ads/components-react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Textarea } from '@/shared/ui/atoms';

import type { ParcelFormValues } from '../../../hooks/useParcelFormSchema';
import type { ParcelFieldName } from '../../../types/parcelEdit';
import { isUnseededField } from '../../../types/parcelEdit';
import { PlanSectionCard } from '../../molecules/PlanSectionCard';

/** Props de `ParcelCommunicationSection`. */
export interface ParcelCommunicationSectionProps {
  readonly form: UseFormReturn<ParcelFormValues>;
  /**
   * Campos que a fonte de leitura não consegue semear — exibidos desabilitados,
   * com a explicação da lacuna. Vem de `useParcelEditSeed`; a criação não passa.
   */
  readonly disabledFields?: readonly ParcelFieldName[] | undefined;
}

/**
 * Seção de comunicação da parcela, compartilhada pela criação e pela edição.
 *
 * Os dois campos são texto livre no legado, mas têm destinos diferentes e a tela
 * declara isso: **Mensagem** acompanha a cobrança enviada ao aluno, **Observação**
 * é anotação interna que aparece na rastreabilidade. Sem essa distinção, um
 * operador escreveria nota interna num campo que o aluno lê.
 */
export function ParcelCommunicationSection({
  form,
  disabledFields,
}: ParcelCommunicationSectionProps) {
  const { t } = useTranslation('individualPayment');
  const { register } = form;

  const isMessageUnseeded = isUnseededField(disabledFields, 'message');

  return (
    <PlanSectionCard title={t('parcelCreate.communication.title')} icon={ChatBubbleLeftRightIcon}>
      <TextField
        label={t('parcelCreate.communication.message')}
        size="md"
        fullWidth
        disabled={isMessageUnseeded}
        custom={{ root: 'w-full', input: 'w-full' }}
        placeholder={t('parcelCreate.communication.messagePlaceholder')}
        // A lacuna de leitura continua sendo dita; o texto explicativo do campo
        // saiu por decisão de UI.
        {...(isMessageUnseeded ? { helper: t('parcelEdit.values.unseededHint') } : {})}
        {...register('message')}
      />

      <div className="flex flex-col gap-nano">
        <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
          {t('parcelCreate.communication.observation')}
        </span>
        <Textarea
          rows={3}
          placeholder={t('parcelCreate.communication.observationPlaceholder')}
          aria-label={t('parcelCreate.communication.observation')}
          {...register('observation')}
        />
      </div>
    </PlanSectionCard>
  );
}
